import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/config';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Search, BookOpen } from 'lucide-react';

export default function ManifestoPage() {
  const { t } = useLang();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/manifesto`);
        if (!c) setTopics(res.data.topics || []);
      } catch (err) {
        void err;
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, []);

  const filtered = topics.filter((tp) =>
    (tp.title + ' ' + tp.summary + ' ' + tp.category).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-16">
          <SectionKicker>{t('nav.manifesto')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
            {t('manifesto.title')}
          </h1>
          <p className="mt-4 max-w-3xl text-[#5B6B82] leading-relaxed">{t('manifesto.lede')}</p>
          <div className="mt-6 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B6B82]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search topics…"
              className="pl-9 bg-white"
              data-testid="manifesto-search-input"
            />
          </div>
        </Container>
      </section>

      <section className="py-14">
        <Container>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((tp) => (
                <button
                  key={tp.slug}
                  onClick={() => setSelected(tp)}
                  className="text-left rounded-xl border border-[#D7E1EF] bg-white p-5 hover:shadow-sm hover:-translate-y-[1px] transition-shadow transition-transform"
                  data-testid="manifesto-topic-card"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center">
                      <BookOpen className="h-4 w-4 text-[#062B5C]" />
                    </div>
                    <Badge variant="outline" className="border-[#D7E1EF] text-[#062B5C]">{tp.category}</Badge>
                  </div>
                  <div className="mt-3 font-display font-semibold text-[#0B1220]">{tp.title}</div>
                  <div className="mt-2 text-sm text-[#5B6B82] leading-relaxed">{tp.summary}</div>
                  <div className="mt-4 text-sm font-medium text-[#062B5C]" data-testid="manifesto-topic-open">
                    {t('manifesto.read_full')} →
                  </div>
                </button>
              ))}
            </div>
          )}
        </Container>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[86vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <Badge variant="outline" className="self-start border-[#D7E1EF] text-[#062B5C]">{selected.category}</Badge>
                <DialogTitle className="font-display text-2xl mt-2">{selected.title}</DialogTitle>
                <DialogDescription className="text-[#5B6B82]">{selected.summary}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <TopicBlock title={t('manifesto.principles_h')} items={selected.principles} />
                <TopicBlock title={t('manifesto.actions_h')} items={selected.actions} />
                <TopicBlock title={t('manifesto.kpis_h')} items={selected.kpis} />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">
                    {t('manifesto.timeline_h')}
                  </div>
                  <p className="mt-2 text-sm text-[#0B1220]">{selected.timeline}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <TricolourRibbon />
    </div>
  );
}

function TopicBlock({ title, items = [] }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">{title}</div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-[#0B1220] leading-relaxed flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F47B20] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
